import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ViewWillLeave } from '@ionic/angular';
import { MaritalStatus, MoradorComRendaDto } from '@usucampeao/lib-reurb-simplificado';
import { LoadingService, NavigationService, ToastService } from '@usucampeao/ui-mobile';
import { AbstractForm, validateCPF } from '@usucampeao/utils-frontend';
import { Subject } from 'rxjs';
import { filter, finalize, takeUntil, tap } from 'rxjs/operators';
import { CadastrosQuery } from '../../../state/cadastros.query';
import { CadastroService } from '../../../state/cadastros.service';

@Component({
  templateUrl: './moradores-com-renda-form.page.html',
  styleUrls: ['./moradores-com-renda-form.page.scss']
})
export class MoradoresComRendaFormPage extends AbstractForm implements OnInit, ViewWillLeave, OnDestroy {
  private readonly ngUnsubscribe$ = new Subject<void>();
  public readonly estadosCivis = Object.values(MaritalStatus);

  private cadastroId: string;
  public form: FormGroup;
  public moradorComRenda: MoradorComRendaDto;

  constructor(
    private readonly cadastrosQuery: CadastrosQuery,
    private readonly cadastrosService: CadastroService,
    private readonly fb: FormBuilder,
    private readonly loadingService: LoadingService,
    private readonly navigationService: NavigationService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly toastService: ToastService,
  ) {
    super();

    this.form = this.fb.group({
      nome: [null, Validators.required],
      rg: [null, Validators.required],
      cpf: [null, Validators.compose([Validators.required, validateCPF])],
      estadoCivil: [null, Validators.required],
      renda: [null, Validators.compose([Validators.required, Validators.min(0)])],
      profissao: [null, Validators.required],
    });
  }

  ngOnInit(): void {
    this.route.params
      .pipe(
        tap(params => this.cadastroId = params.cadastroId),
        filter(params => !!params.cpf),
        tap(params => this.buscarMoradorComRenda(params.cpf)),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe();
  }

  ionViewWillLeave(): void {
    this.ngUnsubscribe$.next();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.complete();
  }

  private buscarMoradorComRenda(cpf: string): void {
    this.loadingService.createLoader();
    this.cadastrosService.listarMoradoresComRenda(this.cadastroId)
      .pipe(
        finalize(() => this.loadingService.dismiss()),
      )
      .subscribe({
        next: moradoresComRenda => {
          this.moradorComRenda = moradoresComRenda.find(morador => morador.cpf === cpf);
          if (this.moradorComRenda) {
            this.form.patchValue({
              nome: this.moradorComRenda.nome,
              rg: this.moradorComRenda.rg,
              cpf: this.moradorComRenda.cpf,
              estadoCivil: this.moradorComRenda.estadoCivil,
              renda: this.moradorComRenda.renda,
              profissao: this.moradorComRenda.profissao,
            });
          }
        },
        error: error => {
          this.toastService.error('Erro ao listar morador. Por favor tente novamente.');
          throw error;
        }
      });
  }

  public submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }

    const formValue = this.form.value;
    const rendaConvertida = this.converterRendaParaNumero(formValue.renda);

    // Cria um novo objeto com a renda convertida
    const dadosParaEnvio = {
      ...formValue,
      renda: rendaConvertida
    };

    this.loadingService.createLoader();
    this.cadastrosService.salvarMoradorComRenda(this.cadastroId, dadosParaEnvio)
      .pipe(
        finalize(() => this.loadingService.dismiss()),
      )
      .subscribe({
        next: () => {
          this.toastService.success('Morador com renda salvo com sucesso').toPromise();
          this.voltar();
        },
        error: error => {
          this.toastService.error('Erro ao salvar morador com renda. Por favor tente novamente.').toPromise();
          throw error;
        }
      });
  }

  private converterRendaParaNumero(rendaString: string): number {
    if (!rendaString) return 0;

    // Remove R$, espaços e converte vírgula para ponto
    const valorLimpo = rendaString
      .replace(/R\$\s*/g, '') // Remove "R$ " e espaços
      .replace(/\./g, '')     // Remove pontos (separadores de milhares)
      .replace(',', '.')      // Converte vírgula para ponto decimal
      .trim();

    return parseFloat(valorLimpo) || 0;
  }


  public voltar(): void {
    this.navigationService.voltar();
  }
}
